import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { timeFormatForUser } from '../../common/ambient'
import tempIconSrc from '../../assets/forecast/temp-icon.svg'
import wbgtIconSrc from '../../assets/forecast/wbgt-icon.svg'
import './FifaEventCard.less'

const ChevronIcon = ({ up }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d={up ? 'M3 10L8 5L13 10' : 'M3 6L8 11L13 6'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M1 4.5H11" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M3.5 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M8.5 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
)

const PinIcon = () => (
  <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
    <path d="M5 0C2.5 0 0.5 2 0.5 4.5C0.5 7.5 5 12 5 12C5 12 9.5 7.5 9.5 4.5C9.5 2 7.5 0 5 0ZM5 6C4.2 6 3.5 5.3 3.5 4.5C3.5 3.7 4.2 3 5 3C5.8 3 6.5 3.7 6.5 4.5C6.5 5.3 5.8 6 5 6Z" fill="currentColor"/>
  </svg>
)

const PrecipIcon = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
    <path d="M6 1L10 7.5C10 10 8.2 12 6 12C3.8 12 2 10 2 7.5L6 1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
)

const FifaEventCard = ({ point, onClick, className, forecast, user }) => {
  const [expanded, setExpanded] = useState(false)

  const team1 = point.teams?.[0]
  const team2 = point.teams?.[1]
  const hasTeams = team1 && team2

  const kickoff = useMemo(() => moment(point.datetime), [point.datetime])

  const hourlyRows = useMemo(() => {
    if (!forecast?.hourly?.data) return []
    return forecast.hourly.data
      .filter(h => moment(h.time * 1000).isSameOrAfter(kickoff))
      .slice(0, 3)
  }, [forecast, kickoff])

  const visibleHours = expanded ? hourlyRows : hourlyRows.slice(0, 1)

  const toggleExpanded = (e) => {
    e.stopPropagation()
    setExpanded(prev => !prev)
  }

  return (
    <div className={classNames('fifa-event-card', className, { expanded })} onClick={onClick}>
      <div className="fifa-card-header">
        <div className="match-title">
          {hasTeams ? (
            <>
              <span className="team-left">
                <span className="team-code">{team1.code || team1.name}</span>
                <span className="team-flag">{team1.flag || '⚽'}</span>
              </span>
              <span className="vs">v</span>
              <span className="team-right">
                <span className="team-flag">{team2.flag || '⚽'}</span>
                <span className="team-code">{team2.code || team2.name}</span>
              </span>
            </>
          ) : (
            <span className="team-code">{point.title}</span>
          )}
        </div>
        <button className="chevron-btn" onClick={toggleExpanded} aria-label={expanded ? 'Collapse' : 'Expand'}>
          <ChevronIcon up={expanded} />
        </button>
      </div>

      <div className="fifa-card-meta">
        {point.datetime && (
          <div className="meta-row">
            <CalendarIcon />
            <span>{moment(point.datetime).format('dddd, MMMM D, YYYY')}</span>
          </div>
        )}
        {(point.address || point.location) && (
          <div className="meta-row">
            <PinIcon />
            <span>{[point.address, point.location].filter(Boolean).join(', ')}</span>
          </div>
        )}
      </div>

      <div className="wbgt-section">
        {expanded ? (
          <p className="wbgt-explanation">
            WBGT monitors heat conditions during outdoor events.{' '}
            <a href="https://heatstress.com/" target="_blank" rel="noopener noreferrer">
              Learn how WBGT works
            </a>
          </p>
        ) : (
          <button className="wbgt-info-btn" onClick={toggleExpanded}>
            What is WBGT heat monitoring
          </button>
        )}
      </div>

      {visibleHours.length > 0 && (
        <div className="hourly-rows">
          {visibleHours.map((h, i) => {
            const isKickoff = i === 0
            const timeLabel = moment(h.time * 1000).format(timeFormatForUser(user))
            return (
              <div key={h.time} className="hourly-row">
                <div className="hour-label">
                  <span className="timeline-dot" />
                  <span className="hour-time">
                    {timeLabel}
                    {isKickoff && <span className="kickoff-label"> (Kickoff)</span>}
                  </span>
                </div>
                <div className="hour-data">
                  <div className={classNames('weather-icon', h.icon)} />
                  <div className="hour-temp">
                    <img src={tempIconSrc} alt="temp" className="data-icon" />
                    <span>{Math.round(h.temperature)}°F</span>
                  </div>
                  {h.wbgt != null && (
                    <div className="hour-wbgt">
                      <span className="wbgt-label"><img src={wbgtIconSrc} alt="wbgt" className="data-icon" /> WBGT</span>
                      <span className="wbgt-value">{Math.round(h.wbgt)}°F</span>
                    </div>
                  )}
                  <div className="hour-precip">
                    <PrecipIcon />
                    <span>{Math.round((h.precipProbability || 0) * 100)}%</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

FifaEventCard.propTypes = {
  point: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    datetime: PropTypes.string,
    location: PropTypes.string,
    address: PropTypes.string,
    teams: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
      flag: PropTypes.string
    }))
  }).isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  forecast: PropTypes.object,
  user: PropTypes.object
}

export default FifaEventCard

FifaEventCard.displayName = 'FifaEventCard'
